import React, { useState, useEffect, useMemo } from 'react';
import { financeService } from '../../services/api';
import CSVImportModal from '../Shared/CSVImportModal';

interface Transaction {
    id: string;
    date: string;
    type: 'INCOME' | 'EXPENSE';
    amount: number;
    category: string;
    description: string;
}

const PRESET_INCOME_CATEGORIES = ['Rent', 'Service Charge', 'Deposit', 'Other Income'];
const PRESET_EXPENSE_CATEGORIES = [
    'Repairs & Maintenance', 'Insurance', 'Mortgage / Finance', 'Letting Agent Fees',
    'Council Tax', 'Utilities', 'Professional Fees', 'Compliance Certificates',
    'Cleaning', 'Landscaping / Garden', 'Management Fee', 'Other Expense'
];

type DatePreset = 'ALL' | 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_QUARTER' | 'THIS_YEAR' | 'CUSTOM';

const TransactionList: React.FC = () => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'ALL' | 'INCOME' | 'EXPENSE'>('ALL');
    const [filterCategory, setFilterCategory] = useState('ALL');
    const [datePreset, setDatePreset] = useState<DatePreset>('ALL');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sortDir, setSortDir] = useState<'DESC' | 'ASC'>('DESC');

    const [newTransaction, setNewTransaction] = useState({
        date: new Date().toISOString().split('T')[0],
        type: 'INCOME',
        amount: '',
        category: '',
        customCategory: '',
        description: ''
    });

    useEffect(() => { fetchTransactions(); }, []);

    const fetchTransactions = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await financeService.getTransactions();
            if (res.data.success) setTransactions(res.data.data || []);
        } catch (error: any) {
            console.error('Error fetching transactions:', error);
            setError(error.response?.data?.message || 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        try {
            const finalCategory = newTransaction.category === '__custom__'
                ? newTransaction.customCategory
                : newTransaction.category;
            const res = await financeService.addTransaction({
                date: newTransaction.date,
                type: newTransaction.type,
                amount: parseFloat(newTransaction.amount),
                category: finalCategory,
                description: newTransaction.description,
            });
            if (res.data.success) {
                setShowAddModal(false);
                fetchTransactions();
                setNewTransaction({ date: new Date().toISOString().split('T')[0], type: 'INCOME', amount: '', category: '', customCategory: '', description: '' });
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to add transaction');
        }
    };

    const handleDelete = async (id: string, date: string, type: string) => {
        if (!window.confirm('Delete this transaction?')) return;
        try {
            const res = await financeService.deleteTransaction(id, date, type);
            if (res.data.success) fetchTransactions();
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to delete transaction');
        }
    };

    // Compute date range from preset
    const getDateRange = (): { from: Date | null; to: Date | null } => {
        const now = new Date();
        if (datePreset === 'THIS_MONTH') {
            return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 0) };
        } else if (datePreset === 'LAST_MONTH') {
            return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1), to: new Date(now.getFullYear(), now.getMonth(), 0) };
        } else if (datePreset === 'THIS_QUARTER') {
            const q = Math.floor(now.getMonth() / 3);
            return { from: new Date(now.getFullYear(), q * 3, 1), to: new Date(now.getFullYear(), q * 3 + 3, 0) };
        } else if (datePreset === 'THIS_YEAR') {
            return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31) };
        } else if (datePreset === 'CUSTOM') {
            return { from: dateFrom ? new Date(dateFrom) : null, to: dateTo ? new Date(dateTo) : null };
        }
        return { from: null, to: null };
    };

    // Unique categories for filter
    const allCategories = useMemo(() => {
        const cats = new Set(transactions.map(t => t.category).filter(Boolean));
        return ['ALL', ...Array.from(cats).sort()];
    }, [transactions]);

    const filtered = useMemo(() => {
        const { from, to } = getDateRange();
        return transactions
            .filter(t => {
                if (filterType !== 'ALL' && t.type !== filterType) return false;
                if (filterCategory !== 'ALL' && t.category !== filterCategory) return false;
                const tDate = new Date(t.date);
                if (from && tDate < from) return false;
                if (to && tDate > to) return false;
                if (searchTerm) {
                    const q = searchTerm.toLowerCase();
                    if (!t.description?.toLowerCase().includes(q) && !t.category?.toLowerCase().includes(q)) return false;
                }
                return true;
            })
            .sort((a, b) => {
                const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
                return sortDir === 'DESC' ? diff : -diff;
            });
    }, [transactions, filterType, filterCategory, datePreset, dateFrom, dateTo, searchTerm, sortDir]);

    const totalIncome = filtered.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.amount, 0);
    const totalExpense = filtered.filter(t => t.type === 'EXPENSE').reduce((s, t) => s + t.amount, 0);
    const net = totalIncome - totalExpense;

    const catOptions = newTransaction.type === 'INCOME' ? PRESET_INCOME_CATEGORIES : PRESET_EXPENSE_CATEGORIES;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex flex-wrap justify-between items-center gap-3 bg-gray-50">
                <h3 className="text-base font-bold text-gray-900">Transactions</h3>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition"
                    >
                        <i className="fas fa-file-import mr-1.5"></i> Bulk Import
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition"
                    >
                        <i className="fas fa-plus mr-1.5"></i> Add Transaction
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="px-4 py-3 bg-slate-50 border-b border-gray-100 flex flex-wrap gap-2 items-end">
                {/* Search */}
                <div className="relative flex-1 min-w-[160px]">
                    <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input
                        type="text"
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Search description / category..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Type */}
                <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden text-xs font-bold">
                    {(['ALL', 'INCOME', 'EXPENSE'] as const).map(t => (
                        <button
                            key={t}
                            onClick={() => setFilterType(t)}
                            className={`px-3 py-1.5 transition ${filterType === t
                                ? t === 'INCOME' ? 'bg-emerald-600 text-white' : t === 'EXPENSE' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'
                                : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                            {t === 'ALL' ? 'All' : t === 'INCOME' ? '↑ Income' : '↓ Expense'}
                        </button>
                    ))}
                </div>

                {/* Category */}
                <select
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white font-medium text-gray-700 focus:ring-emerald-500"
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                >
                    {allCategories.map(c => <option key={c} value={c}>{c === 'ALL' ? 'All Categories' : c}</option>)}
                </select>

                {/* Date Preset */}
                <select
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white font-medium text-gray-700 focus:ring-emerald-500"
                    value={datePreset}
                    onChange={e => setDatePreset(e.target.value as DatePreset)}
                >
                    <option value="ALL">All Time</option>
                    <option value="THIS_MONTH">This Month</option>
                    <option value="LAST_MONTH">Last Month</option>
                    <option value="THIS_QUARTER">This Quarter</option>
                    <option value="THIS_YEAR">This Year</option>
                    <option value="CUSTOM">Custom Range</option>
                </select>

                {/* Custom Date Range */}
                {datePreset === 'CUSTOM' && (
                    <>
                        <input type="date" className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="date" className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </>
                )}

                {/* Sort */}
                <button
                    onClick={() => setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC')}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white font-medium text-gray-700 hover:bg-gray-50 transition"
                    title="Toggle sort order"
                >
                    <i className={`fas fa-sort-amount-${sortDir === 'DESC' ? 'down' : 'up'} mr-1`}></i>
                    Date
                </button>

                {/* Clear */}
                {(filterType !== 'ALL' || filterCategory !== 'ALL' || datePreset !== 'ALL' || searchTerm) && (
                    <button
                        onClick={() => { setFilterType('ALL'); setFilterCategory('ALL'); setDatePreset('ALL'); setSearchTerm(''); setDateFrom(''); setDateTo(''); }}
                        className="text-xs text-gray-400 hover:text-rose-600 transition font-bold"
                    >
                        <i className="fas fa-times mr-1"></i> Clear
                    </button>
                )}
            </div>

            {/* Summary Strip */}
            <div className="grid grid-cols-3 divide-x divide-gray-100 bg-white text-center py-2.5 border-b border-gray-100">
                <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">Income</div>
                    <div className="text-sm font-black text-emerald-600">£{totalIncome.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">Expense</div>
                    <div className="text-sm font-black text-red-600">£{totalExpense.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">Net</div>
                    <div className={`text-sm font-black ${net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {net >= 0 ? '+' : ''}£{net.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{error}</div>}

            {loading ? (
                <div className="p-8 text-center text-gray-400 text-sm">Loading transactions...</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider">Type</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider">Category</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider">Description</th>
                                <th className="px-4 py-2.5 text-right text-[10px] font-black text-gray-400 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-2.5 text-right text-[10px] font-black text-gray-400 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50">
                            {filtered.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-400">No transactions match your filters.</td></tr>
                            ) : (
                                filtered.map(t => (
                                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700 font-medium">{t.date}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${t.type === 'INCOME' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                {t.type === 'INCOME' ? '↑' : '↓'} {t.type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">{t.category}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500 max-w-[220px] truncate">{t.description}</td>
                                        <td className={`px-4 py-3 whitespace-nowrap text-xs text-right font-black ${t.type === 'INCOME' ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {t.type === 'INCOME' ? '+' : '-'}£{t.amount.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right">
                                            <button onClick={() => handleDelete(t.id, t.date, t.type)} className="text-[10px] font-bold text-gray-300 hover:text-red-600 transition">
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {filtered.length > 0 && (
                        <div className="px-4 py-2 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
                            Showing {filtered.length} of {transactions.length} transactions
                        </div>
                    )}
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white flex items-center justify-between">
                            <h3 className="text-base font-bold">Add Transaction</h3>
                            <button onClick={() => setShowAddModal(false)} className="text-white/70 hover:text-white text-xl">&times;</button>
                        </div>
                        <form onSubmit={handleAdd} className="p-6 space-y-4">
                            {/* Type Toggle */}
                            <div className="flex bg-gray-100 p-1 rounded-xl">
                                <button type="button" onClick={() => setNewTransaction({ ...newTransaction, type: 'INCOME', category: '' })}
                                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${newTransaction.type === 'INCOME' ? 'bg-emerald-600 text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}>
                                    ↑ Income
                                </button>
                                <button type="button" onClick={() => setNewTransaction({ ...newTransaction, type: 'EXPENSE', category: '' })}
                                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${newTransaction.type === 'EXPENSE' ? 'bg-red-600 text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}>
                                    ↓ Expense
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Date</label>
                                    <input type="date" required className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                                        value={newTransaction.date} onChange={e => setNewTransaction({ ...newTransaction, date: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Amount (£)</label>
                                    <input type="number" step="0.01" required placeholder="0.00" className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                                        value={newTransaction.amount} onChange={e => setNewTransaction({ ...newTransaction, amount: e.target.value })} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Category</label>
                                <select required className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                                    value={newTransaction.category} onChange={e => setNewTransaction({ ...newTransaction, category: e.target.value })}>
                                    <option value="">Select category...</option>
                                    {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                    <option value="__custom__">+ Custom Category</option>
                                </select>
                            </div>

                            {newTransaction.category === '__custom__' && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Custom Category Name</label>
                                    <input type="text" required placeholder="e.g. Legal Fees" className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                                        value={newTransaction.customCategory} onChange={e => setNewTransaction({ ...newTransaction, customCategory: e.target.value })} />
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Description (optional)</label>
                                <textarea rows={2} placeholder="Additional details..." className="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                                    value={newTransaction.description} onChange={e => setNewTransaction({ ...newTransaction, description: e.target.value })} />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowAddModal(false)}
                                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition">Cancel</button>
                                <button type="submit"
                                    className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition">Save Transaction</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <CSVImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={async (data) => {
                    const validatedData = data.map((row, idx) => {
                        const type = String(row.type || '').toUpperCase().trim();
                        if (type !== 'INCOME' && type !== 'EXPENSE') throw new Error(`Row ${idx + 1}: Type must be INCOME or EXPENSE`);
                        const amount = parseFloat(String(row.amount || '0').replace(/[^0-9.-]+/g, ''));
                        if (isNaN(amount)) throw new Error(`Row ${idx + 1}: Amount must be a valid number`);
                        return { ...row, type, amount };
                    });
                    await financeService.bulkImport(validatedData);
                    fetchTransactions();
                }}
                title="Bulk Import Transactions"
                headerRequirements={[
                    { id: 'date', label: 'Date', required: true, description: 'Transaction date (YYYY-MM-DD)', example: '2024-03-25' },
                    { id: 'type', label: 'Type', required: true, description: 'INCOME or EXPENSE', example: 'INCOME' },
                    { id: 'amount', label: 'Amount', required: true, description: 'Numerical value', example: '1250.00' },
                    { id: 'category', label: 'Category', required: true, description: 'Classification', example: 'Rent' },
                    { id: 'description', label: 'Description', required: false, description: 'Additional details', example: 'Monthly rent Flat A' }
                ]}
                templateHeaders={['date', 'type', 'amount', 'category', 'description']}
                sampleData="2024-03-01,INCOME,1200.00,Rent,Monthly rent for Flat A"
            />
        </div>
    );
};

export default TransactionList;
